import React from 'react';
import PropTypes from 'prop-types';
import css from './ReadMoreLessText.css';
import StyledButton from 'common/StyledButton';
import striptags from 'striptags';
import { isEmptyString } from "../utils/text";
import classnames from "classnames";

/**
 * When the text goes beyond the defined max-height, it will fade out
 * and a button to toggle display of full text will be shown.
 * Example of default styling for this component can be seen on
 * the Progress Survey page (survey instructions).
 *
 * The number of lines of text before fading out is set with max-height
 * and line-height in css:
 *    Example: 66px / 22px = 3 lines of text before fading out
 *    line-height: 22px;
 *    max-height: 66px;
 *
 * @param {object} props text to be displayed, custom css to change
 *    default styling and text fade-out restriction of component.
 * @returns {React.Component} text with toggle show more/less button
 */
export default class ReadMoreLessText extends React.Component {

  constructor() {
    super();
    this.state = { hasMoreText: false, isShowingMore: false };
    this.pRef = React.createRef();
    this.showMoreRef = React.createRef();
    this.showLessRef = React.createRef();
  }

  componentDidMount() {
    this.computeHasMoreText();
  }

  componentDidUpdate(prevProps, prevState) {
    if ( this.props.text !== prevProps.text ) this.computeHasMoreText();
    else if ( prevState.isShowingMore ) this.setState({isShowingMore: false});
  }

  computeHasMoreText() {
    if ( !this.pRef || this.pRef.current === null ) return;
    const { offsetHeight, scrollHeight } = this.pRef.current.parentNode;
    if ( scrollHeight > offsetHeight ) this.setState({hasMoreText: true, isShowingMore: false})
    else this.setState({hasMoreText: false, isShowingMore: false});
  }

  toggleText() {
    this.setState({isShowingMore: !this.state.isShowingMore}, () => {
      if ( !this.state.isShowingMore && this.showMoreRef && this.showMoreRef.current ) this.showMoreRef.current.focus();
      else if ( this.state.isShowingMore && this.showLessRef && this.showLessRef.current ) this.showLessRef.current.focus();
    });
  }

  get paragraph() {
    return <p ref={this.pRef}>
          <span className={this.state.hasMoreText ? classnames('more-text', css['span-has-more-text'], { [this.props.customCss.hasMoreTextCss]: !isEmptyString(this.props.customCss.hasMoreTextCss) }) : ''}
                dangerouslySetInnerHTML={{__html: this.props.allowHtml ? this.props.text : striptags(this.props.text, [])}} 
          />{this.showLessButton}
    </p>;
  }

  get showMoreButton() {
    if ( !this.state.hasMoreText ) return null;
    return (
      <StyledButton 
        asLink 
        ref={this.showMoreRef}
        className={classnames(css['more'], { [this.props.customCss.showMoreCss]: !isEmptyString(this.props.customCss.showMoreCss) })}
        onClick={this.toggleText.bind(this)}>
        More
      </StyledButton>
    );
  }

  get showLessButton() {
    if ( !this.state.isShowingMore ) return null;
    return (
      <StyledButton 
        asLink 
        ref={this.showLessRef}
        className={classnames(css['less'], { [this.props.customCss.showLessCss]: !isEmptyString(this.props.customCss.showLessCss) })}
        onClick={this.toggleText.bind(this)}>
        Less
      </StyledButton>
    );
  }

  get fixedSizeParagraph() {
    return (
      <div className={classnames(css['fixed'], { [this.props.customCss.fixedCss]: !isEmptyString(this.props.customCss.fixedCss) })}>
        {this.paragraph}
        {this.showMoreButton}
      </div>
    );
  }

  get fullParagraph() {
    return (
      <div className={classnames(css['full'], { [this.props.customCss.fullCss]: !isEmptyString(this.props.customCss.fullCss) })}>
        {this.paragraph}
      </div>
    );
  }
  
  render() {
    return this.state.isShowingMore ? this.fullParagraph : this.fixedSizeParagraph;
  }
}

// Do not set allowHtml to true unless you are certain that the props.text has been properly sanitized and HTML support is required!
ReadMoreLessText.defaultProps = {
  allowHtml: false,
  customCss: {
    fullCss: '',
    fixedCss: '',
    hasMoreTextCss: '',
    showMoreCss: '',
    showLessCss: ''
  }
};

ReadMoreLessText.propTypes = {
  text: PropTypes.string.isRequired,
  allowHtml: PropTypes.bool.isRequired,
  customCss: PropTypes.shape({
    fullCss: PropTypes.string,
    fixedCss: PropTypes.string,
    hasMoreTextCss: PropTypes.string,
    showMoreCss: PropTypes.string,
    showLessCss: PropTypes.string
  })
};
